import {
  useState, forwardRef, useEffect, useMemo,
  useRef
} from "react";

import * as Select from "@radix-ui/react-select";

import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { SvgIcon, Accordion } from "@/components/common";

import {
  selectActiveCompany,
  setActiveCompany,
  setCompanies,
} from "@/store/appSlice";
import { useUser } from "@/hooks";
import { getCompanies } from "@/api/finance/companyApi";
import { getCompany } from "@/api/user/authApi";
import { buildTree } from "../../utils/finance/helpers";
import { updateBookOfBusiness } from "@/api/admin/companyAccountApi";

function SearchInput({ onSearch }) {
  const [value, setValue] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [value]);

  const handleChange = (event) => {
    event.stopPropagation();
    const newValue = event.target.value;
    setValue(newValue);
    onSearch(newValue);
  };

  const handleKeyDown = (event) => {
    event.stopPropagation();
  };

  const handleClick = (event) => {
    event.stopPropagation();
    inputRef.current.focus();
  };

  return (
    <div role="presentation" className="SearchInputContainer" onClick={handleClick}>
      <SvgIcon icon="search" />
      <input
        ref={inputRef}
        type="text"
        placeholder="Search"
        className="SearchInput"
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
      />
    </div>
  );
}

function GroupCollapsibleMenu({ key, label, options }) {
  return (
    <Accordion
      className="CollapsibleRoot CollapsibleChild"
      key={key}
      title={label}
    >
      <Select.Group className="CollapsibleGroup">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </Select.Group>
    </Accordion>
  );
}

function ParentGroupCollapsibleMenu({
  label: groupLabel,
  options: groupOptions,
}) {
  return (
    <Accordion
      key="ParentAccordion"
      className="CollapsibleRoot"
      title={groupLabel}
    >
      <div className="CollapsibleContainer">
        {groupOptions?.map(({ label: tagLabel, subRows: tagOptions }) => (
          <GroupCollapsibleMenu
            key={tagLabel}
            label={tagLabel}
            options={tagOptions}
          />
        ))}
      </div>
    </Accordion>
  );
}

function SelectOptionKey({ children, className }) {
  return <div className={className}>{children}</div>;
}

const SelectItem = forwardRef(
  ({ children, className, ...props }, forwardedRef) => (
    <Select.Item className="Item" {...props} ref={forwardedRef}>
      <Select.ItemText>{children}</Select.ItemText>
      <Select.ItemIndicator className="ItemIndicator">
        <SvgIcon icon="tick-icon" />
      </Select.ItemIndicator>
      <SelectOptionKey className="ItemKey">↵ Enter</SelectOptionKey>
    </Select.Item>
  )
);

function CompanySwitcher() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { user } = useUser();
  const activeCompany = useSelector(selectActiveCompany);
  const location = useLocation();
  const navigate = useNavigate();

  const { data: companiesData, isLoading } = useQuery(
    ["company", user?.userId],
    () => getCompanies(),
    { enabled: Boolean(user?.userId) }
  );

  const companies = useMemo(() => {
    if (!user || !companiesData || isLoading) {
      return [];
    }

    const userCompanyIds = user?.companies.map((c) => c.id);

    return companiesData?.data.filter((c) => userCompanyIds.includes(c.id)).map((c) => ({
      ...c,
      label: c.name,
      value: c.id
    }));
  }, [companiesData, user]);

  const companyOptions = useMemo(() => buildTree(companies), [companies]);

  const [filteredOptions, setFilteredOptions] = useState([]);

  useEffect(() => {
    if (user && companiesData) {
      const formattedCompanies = companiesData?.data.map((company) => ({
        ...company,
        value: company.id,
        label: company.name,
      }));
      dispatch(setCompanies(formattedCompanies));
    }
  }, [companiesData, user]);

  useEffect(() => {
    if (companies.length) {
      const { defaultBookOfBusiness } = user;
      const activeCompanyId = getCompany();
      let selectedCompany = null;

      if (activeCompanyId) {
        selectedCompany = companies.find((c) => c.id === activeCompanyId);
      }

      if (!selectedCompany && defaultBookOfBusiness) {
        selectedCompany = companies.find(
          (c) => c.name.toLowerCase() === defaultBookOfBusiness.toLowerCase()
        );
      }

      if (!selectedCompany) {
        const [firstCompany, ...rest] = companies;
        selectedCompany = firstCompany;
      }

      window.localStorage.setItem(
        "date_format",
        selectedCompany?.companySettings?.dateFormat
      );
      dispatch(setActiveCompany(selectedCompany));
    }
  }, [companies, user]);

  const updateBookOfBusinessApi = async () => {
    try {
      await updateBookOfBusiness();
    } catch (error) {
      console.error("Error updating book of business:", error);
    }
  };

  const handleSwitchCompany = async (companyId) => {
    if (companies.length) {
      const selectedCompany = companies.find(((c) => c.value === companyId));
      dispatch(setActiveCompany(selectedCompany));
      const paths = location.pathname.split("/");
      const slicedPaths = paths.splice(1, 2);
      const pathName = `/${slicedPaths.join("/")}`;
      if (!pathName.includes("reports")) {
        navigate(pathName);
      }
      await queryClient.invalidateQueries();

      await updateBookOfBusinessApi();
    }
  };

  useEffect(() => {
    setFilteredOptions(companyOptions);
  }, [companyOptions]);

  const filterOptions = (searchString) => {
    if (!searchString.trim()) {
      setFilteredOptions(companyOptions);
      return;
    }

    const searchLower = searchString.toLowerCase();

    const filteredCompanies = companyOptions.map((group) => {
      const filteredSubRows = group.subRows.map((category) => {
        const matchingOptions = category.subRows.filter((option) =>
          option.label.toLowerCase().includes(searchLower)
        );

        return matchingOptions.length > 0 ? {
          ...category,
          subRows: matchingOptions
        } : null;
      }).filter(Boolean);

      return filteredSubRows.length > 0 ? {
        ...group,
        subRows: filteredSubRows
      } : null;
    }).filter(Boolean);

    setFilteredOptions(filteredCompanies);
  };

  return (
    <Select.Root value={activeCompany?.value} onValueChange={handleSwitchCompany}>
      <Select.Trigger className="Trigger" aria-label="Food">
        <Select.Value placeholder="Select company">{activeCompany?.label}</Select.Value>
        <Select.Icon className="Icon">
          <SvgIcon icon="arrow-down-icon" />
        </Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content className="Content" position="popper">
          <Select.Viewport className="Viewport">
            <SearchInput onSearch={filterOptions} />
            <div className="CollapsibleContainer">
              {filteredOptions.map((o) => (
                <ParentGroupCollapsibleMenu
                  key={o.label}
                  label={o.label}
                  options={o.subRows}
                />
              ))}
            </div>
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}

export default CompanySwitcher;
